'use client';
import { useState } from 'react';
import axios from 'axios';
import EditableText from './EditableText';
import ColorPicker from './ColorPicker';
import { useEditMode } from './EditModeProvider';
import { useSmartCaptcha } from '../lib/useSmartCaptcha';
import { isCompletePhone, toE164 } from '../lib/phoneMask';
import PhoneInput from './ui/PhoneInput';

const ConnectionStepsForm = () => {
    const { isEditMode, getContent } = useEditMode();
    const { getCaptchaToken } = useSmartCaptcha();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        sphere: ''
    });
    const [agreement, setAgreement] = useState(false);
    const [status, setStatus] = useState({ loading: false, error: null, success: false });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreement) {
            setStatus({ loading: false, error: 'Необходимо согласие с условиями', success: false });
            return;
        }
        if (!isCompletePhone(formData.phone)) {
            setStatus({ loading: false, error: 'Введите телефон полностью', success: false });
            return;
        }

        setStatus({ loading: true, error: null, success: false });
        try {
            const smartToken = await getCaptchaToken();
            const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';
            await axios.post(`${apiUrl}/leads`, { ...formData, phone: toE164(formData.phone), smartToken });
            
            setStatus({ loading: false, error: null, success: true });
            setFormData({ name: '', phone: '', sphere: '' });
            setAgreement(false);
            
            setTimeout(() => {
                setStatus(s => ({ ...s, success: false }));
            }, 5000);
        } catch (error) {
            console.error('Submit error:', error);
            setStatus({ 
                loading: false, 
                error: 'Произошла ошибка при отправке заявки. Пожалуйста, попробуйте позже.', 
                success: false 
            });
        }
    };

    // Получаем значение фона и гарантируем, что это градиент для корректного наложения
    const bgValue = getContent('connection_steps_gradient', 'linear-gradient(101.46deg, #13547A -7.74%, #80D0C7 132.4%)');
    const safeBgImage = bgValue.includes('gradient') ? bgValue : `linear-gradient(0deg, ${bgValue}, ${bgValue})`;

    return (
        <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div 
                className="rounded-[32px] sm:rounded-[40px] p-6 sm:p-10 md:p-14 shadow-2xl relative overflow-hidden"
                style={{ 
                    backgroundImage: `linear-gradient(180deg, transparent 20%, rgba(255,255,255,0) 20%), ${safeBgImage}` 
                }}
            >
                {isEditMode && (
                    <div className="absolute top-4 right-4 z-50">
                        <ColorPicker
                            settings={[
                                { key: 'connection_steps_gradient', label: 'Фон секции (Градиент/Цвет)' },
                                { key: 'connection_steps_btn_color', label: 'Цвет кнопки' }
                            ]}
                        />
                    </div>
                )}

                <EditableText
                    contentKey="steps_title"
                    defaultValue="Три шага для подключения"
                    tagName="h2"
                    className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 sm:mb-12"
                />

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 sm:mb-12">
                    {/* Card 1 */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col h-full">
                        <EditableText
                            contentKey="step1_title"
                            defaultValue="Оставить заявку"
                            tagName="h3"
                            className="text-xl sm:text-2xl font-bold text-[#0F172A] mb-3"
                        />
                        <EditableText
                            contentKey="step1_desc"
                            defaultValue="Заполните форму на сайте - укажите контакты и сферу деятельности. Мы свяжемся с вами, чтобы согласовать удобное время встречи"
                            tagName="p"
                            className="text-[#334155] text-sm sm:text-base leading-relaxed"
                        />
                    </div>

                    {/* Card 2 */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col h-full">
                        <EditableText
                            contentKey="step2_title"
                            defaultValue="Онлайн-демонстрация"
                            tagName="h3"
                            className="text-xl sm:text-2xl font-bold text-[#0F172A] mb-3"
                        />
                        <EditableText
                            contentKey="step2_desc"
                            defaultValue="Проведем видеовстречу: покажем работу системы САНЭКС в реальном времени, рассчитаем нужное число датчиков под ваши задачи"
                            tagName="p"
                            className="text-[#334155] text-sm sm:text-base leading-relaxed"
                        />
                    </div>

                    {/* Card 3 */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col h-full">
                        <EditableText
                            contentKey="step3_title"
                            defaultValue="Договор и запуск"
                            tagName="h3"
                            className="text-xl sm:text-2xl font-bold text-[#0F172A] mb-3"
                        />
                        <EditableText
                            contentKey="step3_desc"
                            defaultValue="Оформим документы, доставим оборудование (или установим сами) и поможем быстро внедрить ваш бизнес в цифровую среду"
                            tagName="p"
                            className="text-[#334155] text-sm sm:text-base leading-relaxed"
                        />
                    </div>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <input
                            type="text"
                            name="name"
                            placeholder="Имя представителя"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full bg-white rounded-full px-5 py-3.5 text-sm sm:text-base text-gray-900 placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#10B981] border-none shadow-sm"
                        />
                        <PhoneInput
                            name="phone"
                            value={formData.phone}
                            onChange={(phone) => setFormData({ ...formData, phone })}
                            required
                            className="w-full bg-white rounded-full px-5 py-3.5 text-sm sm:text-base text-gray-900 placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#10B981] border-none shadow-sm"
                        />
                        <input
                            type="text"
                            name="sphere"
                            placeholder="Сфера деятельности"
                            value={formData.sphere}
                            onChange={handleChange}
                            className="w-full bg-white rounded-full px-5 py-3.5 text-sm sm:text-base text-gray-900 placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#10B981] border-none shadow-sm"
                        />
                    </div>

                    <div className="flex items-start mb-8">
                        <div className="flex items-center h-5 mt-1">
                            <input
                                id="agreement"
                                type="checkbox"
                                checked={agreement}
                                onChange={(e) => setAgreement(e.target.checked)}
                                required
                                className="w-4 h-4 border border-gray-300 rounded bg-white focus:ring-3 focus:ring-[#10B981] text-[#10B981] cursor-pointer"
                            />
                        </div>
                        <label htmlFor="agreement" className="ml-3 text-xs sm:text-sm text-white/90 cursor-pointer">
                            Я принимаю условия{' '}
                            <a href="https://ses-rb.ru/polzovatelskoe-soglashenie/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">пользовательского соглашения</a>,{' '}
                            <a href="https://ses-rb.ru/politika-konfidentsialnosti/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">политики конфиденциальности</a> и{' '}
                            <a href="https://ses-rb.ru/soglasiye-na-obrabotku-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">согласия на обработку персональных данных</a>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={status.loading}
                        className="w-full transition-opacity duration-300 hover:opacity-90 text-white font-medium text-lg rounded-full py-4 shadow-lg disabled:opacity-70"
                        style={{ backgroundColor: getContent('connection_steps_btn_color', '#17c6a6') }}
                    >
                        {status.loading ? 'Отправка...' : (
                            <EditableText
                                contentKey="connection_steps_btn_text"
                                defaultValue="Оставить заявку"
                                tagName="span"
                            />
                        )}
                    </button>

                    {status.success && (
                        <div className="mt-4 p-4 bg-green-500/20 border border-green-400 text-white rounded-xl text-center">
                            Ваша заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.
                        </div>
                    )}
                    {status.error && (
                        <div className="mt-4 p-4 bg-red-500/20 border border-red-400 text-white rounded-xl text-center">
                            {status.error}
                        </div>
                    )}
                </form>
            </div>
        </section>
    );
};

export default ConnectionStepsForm;